import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { apiSlice } from "../../api/api";
import { useAcknowledgePrivacyPolicyMutation } from "../../api/userSlice";
import { toastMessage } from "../utils";
import "./ConsentPopup.css";

const PRIVACY_POLICY_URL = "https://lynkit.in/privacy_policy";
const TERMS_OF_USE_URL = "https://lynkit.in/general_terms_of_use";

export default function ConsentPopup({ userDetail, refetchUserDetail }) {
	const [isChecked, setIsChecked] = useState(false);
	const [acknowledgePrivacyPolicy, { isLoading }] = useAcknowledgePrivacyPolicyMutation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const handleLogout = useCallback(() => {
		dispatch(apiSlice.util.resetApiState());
		localStorage.removeItem("secretkey");
		sessionStorage.removeItem("secretkey");
		localStorage.removeItem("userDetail");
		window.location.replace("/login");
	}, [dispatch]);

	useEffect(() => {
		const currentHistoryState = window.history.state || {};
		if (!currentHistoryState.privacyConsentGuard) {
			window.history.pushState(
				{ ...currentHistoryState, privacyConsentGuard: true },
				"",
				window.location.href
			);
		}

		window.addEventListener("popstate", handleLogout);
		return () => window.removeEventListener("popstate", handleLogout);
	}, [handleLogout]);

	const handleSubmit = async () => {
		if (!isChecked) {
			toastMessage(false, "Please accept the Privacy Policy and Terms of Use to continue.");
			return;
		}

		try {
			const response = await acknowledgePrivacyPolicy({}).unwrap();

			if (response?.error || response?.success === false) {
				toastMessage(false, response?.message || "Failed to save consent.");
				return;
			}

			const pendingToken = sessionStorage.getItem("secretkey");
			if (pendingToken) {
				localStorage.setItem("secretkey", pendingToken);
				sessionStorage.removeItem("secretkey");
			}

			toastMessage(true, response?.message || "Consent submitted successfully.");
			await refetchUserDetail();
			navigate("/welcome", { replace: true });
		} catch (error) {
			toastMessage(false, error?.data?.message || "Failed to save consent.");
		}
	};

	return (
		<div className="lynkid-consent-modal__backdrop">
			<div className="lynkid-consent-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="lynkid-consent-modal-title">
				<button type="button" className="lynkid-consent-modal__close" onClick={handleLogout} aria-label="Close">
					<CloseIcon fontSize="small" />
				</button>
				<div className="lynkid-consent-modal__icon">
					<GppGoodOutlinedIcon fontSize="small" />
				</div>

				<h2 className="lynkid-consent-modal__title" id="lynkid-consent-modal-title">
					Updated Terms of Use and Privacy Policy
				</h2>
				
				<p className="lynkid-consent-modal__subtitle">
					{/* To continue, please confirm you agree to how we collect and process your personal data. */}
					By continuing, you confirm that you understand and accept the privacy policy and consent to the collection and processing of your information for application use.
				</p>

				<label className={`lynkid-consent-modal__row ${isChecked ? "lynkid-consent-modal__row--checked" : ""}`}>
					<input
						type="checkbox"
						checked={isChecked}
						onChange={() => setIsChecked((prev) => !prev)}
						className="lynkid-consent-modal__checkbox"
					/>
					<span>
						I consent to the processing of my personal data per the{" "}
						<a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>Privacy Policy</a>
						{" "}and{" "}
						<a href={TERMS_OF_USE_URL} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>Terms of Use</a>.
					</span>
				</label>

				<div className="lynkid-consent-modal__actions">
					<button
						type="button"
						onClick={handleSubmit}
						disabled={isLoading || !isChecked}
						className="lynkid-consent-modal__submit"
					>
						{isLoading ? "Submitting..." : "I Agree & Continue"}
					</button>
				</div>
			</div>
		</div>
	);
}
